import React, {CSSProperties, useRef} from 'react'
import {ToolTip} from '../index.js'
import {FieldProps} from './types.js'
import {choiceTokens, controlLabel} from './choiceStyles.js'
import ControlLabel from './ControlLabel.js'

interface RadioOption {
    value: string | number
    label?: string
    icon?: string
    tooltip?: string
}

interface RadioGroupProps extends FieldProps {
    options: RadioOption[]
    value: string | number
    onChange?: (event: {target: {value: string | number}}) => void
}

const RadioGroup: React.FC<RadioGroupProps> = ({
    options,
    value,
    onChange,
    label,
    info,
    style,
    disabled = false,
    readOnly = false
}) => {
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])

    // Read only keeps the normal look but ignores clicks and keys, like disabled
    const interactive = !disabled && !readOnly

    // Like a native radio group only the selected option is in the tab order (the first one when none is selected)
    const selectedIndex = options.findIndex((option) => option.value === value)
    const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0

    // Arrow keys move the selection to the previous/next option and wrap around, like a native radio group
    const handleArrowKey = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0

        if (!step || !interactive || options.length === 0) {
            return
        }

        event.preventDefault()

        const nextIndex = (index + step + options.length) % options.length
        optionRefs.current[nextIndex]?.focus()
        onChange?.({target: {value: options[nextIndex].value}})
    }

    const handleClickOption = (optionValue: string | number, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (interactive) {
            onChange?.({target: {value: optionValue}})
        }
    }

    const containerStyle: CSSProperties = {
        display: 'flex',
        flex: 1,
        borderRadius: 4,
        background: disabled ? choiceTokens.groupDisabledBackground : choiceTokens.groupBackground,
        padding: choiceTokens.groupPadding,
        ...choiceTokens.margins.radioGroup,
        ...style
    }

    if (containerStyle.width) {
        containerStyle.flex = undefined
    }

    // With a label the outer layout (flex, width, margins) moves to a wrapper, so the label sits above the group
    const hasLabel = !!(label || info)

    const wrapperStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        flex: containerStyle.flex,
        marginLeft: containerStyle.marginLeft,
        marginRight: containerStyle.marginRight,
        marginBottom: containerStyle.marginBottom,
        width: containerStyle.width
    }

    if (hasLabel) {
        containerStyle.flex = undefined
        containerStyle.marginLeft = 0
        containerStyle.marginRight = 0
        containerStyle.marginBottom = 0
        containerStyle.width = undefined
    }

    const defaultOptionStyle: CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        background: 'transparent',
        cursor: interactive ? 'pointer' : 'default',
        // The bar with its padding is as tall as a field
        height: choiceTokens.height - 2 * choiceTokens.groupPadding,
        paddingLeft: 12,
        paddingRight: 12,
        fontSize: 14,
        border: 'none',
        borderRadius: 4,
        color: choiceTokens.groupForeground,
        transition: 'background 0.2s ease, color 0.2s ease'
    }

    const getOptionStyle = (optionValue: string | number): CSSProperties => {
        const isActive = optionValue === value

        if (isActive && disabled) {
            return {
                ...defaultOptionStyle,
                background: choiceTokens.groupSelectedDisabledBackground,
                color: choiceTokens.groupForeground
            }
        }

        if (isActive) {
            return {
                ...defaultOptionStyle,
                background: choiceTokens.groupSelectedBackground,
                color: choiceTokens.groupSelectedForeground
            }
        }

        return defaultOptionStyle
    }

    const group = (
        <div style={containerStyle} role='radiogroup' aria-disabled={disabled || undefined} aria-readonly={readOnly || undefined}>
            {options.map((option, index) => (
                <ToolTip text={option.tooltip} key={`${option.value}-${index}-tooltip`} style={{flex: 1, height: '100%'}}>
                    <div
                        style={getOptionStyle(option.value)}
                        onClick={(event) => handleClickOption(option.value, event)}
                        role='radio'
                        aria-checked={option.value === value}
                        ref={(element) => {
                            optionRefs.current[index] = element
                        }}
                        tabIndex={disabled || index !== tabbableIndex ? -1 : 0}
                        onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && interactive) {
                                event.preventDefault()
                                handleClickOption(option.value, event)
                            } else {
                                handleArrowKey(index, event)
                            }
                        }}
                    >
                        {option.icon && (
                            <i
                                className={option.icon}
                                style={{
                                    fontSize: 20,
                                    marginRight: option.label ? 10 : 0
                                }}
                            />
                        )}
                        {option.label}
                    </div>
                </ToolTip>
            ))}
        </div>
    )

    if (!hasLabel) {
        return group
    }

    return (
        <div style={wrapperStyle}>
            <ControlLabel style={{marginBottom: controlLabel.gapAbove}} info={info} disabled={disabled}>
                <div style={{whiteSpace: 'nowrap'}}>{label}</div>
            </ControlLabel>

            {group}
        </div>
    )
}

export default RadioGroup
